import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Package, CheckCircle, Wrench } from 'lucide-react';
import { useMaterial } from '../hooks/useApi';
import analytics from '../utils/analytics';
import { usePageTitle } from '../hooks/usePageTitle';

const MaterialDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: material, loading, error } = useMaterial(id || '');

  // 设置页面标题和描述
  usePageTitle(
    material ? material.title : '器件详情',
    material ? material.description : '查看器件材料的详细信息和技术参数'
  );

  // 材料查看埋点
  useEffect(() => {
    if (material) {
      analytics.material.view(material.title, material.category);
    }
  }, [material]);

  // 加载状态
  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-gray-50">
        <div className="container-max section-padding">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <span className="ml-4 text-gray-600">加载中...</span>
          </div>
        </div>
      </div>
    );
  }

  // 错误状态或材料不存在
  if (error || !material) {
    return (
      <div className="pt-16 min-h-screen bg-gray-50">
        <div className="container-max section-padding">
          <div className="text-center py-20">
            <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              器件不存在
            </h3>
            <p className="text-gray-500 mb-4">
              抱歉,您查找的器件不存在或已被删除
            </p>
            <button
              onClick={() => navigate('/materials')}
              className="btn-primary"
            >
              返回器件列表
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen bg-gray-50">
      {/* 返回按钮 */}
      <div className="bg-white border-b">
        <div className="container-max py-4">
          <Link
            to="/materials"
            className="inline-flex items-center text-gray-600 hover:text-primary-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回器件列表
          </Link>
        </div>
      </div>

      {/* 材料详情内容 */}
      <div className="container-max section-padding">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto"
        >
          {/* 头部信息 */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
              {/* 左侧：图片 */}
              <div className="flex items-center justify-center bg-gray-50 rounded-lg p-8">
                <img
                  src={material.image || '/images/placeholder-product.jpg'}
                  alt={material.title}
                  className="max-w-full max-h-96 object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/images/placeholder-product.jpg';
                  }}
                />
              </div>

              {/* 右侧：基本信息 */}
              <div className="flex flex-col justify-center">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  {material.title}
                </h1>

                {material.title_en && (
                  <p className="text-lg text-gray-600 mb-6">
                    {material.title_en}
                  </p>
                )}

                {/* 类别标签 */}
                {material.category && (
                  <div className="mb-6">
                    <span className="px-4 py-2 bg-primary-50 text-primary-700 rounded-full text-sm font-medium">
                      {material.category === 'measurement' && '测量仪表'}
                      {material.category === 'control' && '控制设备'}
                      {material.category === 'refractory' && '耐火材料'}
                      {material.category === 'other' && '其他'}
                    </span>
                  </div>
                )}

                {/* 产品描述 */}
                <div className="text-gray-700 leading-relaxed">
                  {material.description}
                </div>

                {material.description_en && (
                  <div className="text-gray-600 leading-relaxed mt-4 pt-4 border-t">
                    {material.description_en}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 规格参数 */}
          {material.specifications && Object.keys(material.specifications).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-xl shadow-sm p-8 mb-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Wrench className="w-6 h-6 mr-2 text-primary-600" />
                技术规格
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(material.specifications).map(([key, value]) => (
                  <div key={key} className="flex border-b border-gray-200 pb-3">
                    <span className="font-medium text-gray-700 w-1/3 flex-shrink-0">
                      {key}
                    </span>
                    <span className="text-gray-600 flex-1">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* 产品特点 */}
          {material.features && material.features.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm p-8 mb-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <CheckCircle className="w-6 h-6 mr-2 text-primary-600" />
                产品特点
              </h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {material.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 mr-3 mt-0.5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* 应用场景 */}
          {material.applications && material.applications.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-8 mb-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Package className="w-6 h-6 mr-2 text-primary-600" />
                应用场景
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {material.applications.map((application, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-4 border border-primary-100"
                  >
                    <p className="text-gray-800 font-medium">{application}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* 底部操作 */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
            <Link
              to="/materials"
              className="btn-secondary inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回器件列表
            </Link>
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center"
            >
              <Package className="w-4 h-4 mr-2" />
              咨询购买
            </Link>
          </div>
        </motion.article>
      </div>
    </div>
  );
};

export default MaterialDetailPage;
